import React, { useState, useEffect, useRef } from "react";
import { View, Text, Pressable, Animated, Dimensions, StyleSheet, ScrollView, Platform } from "react-native";
import { useNavigation } from "@react-navigation/native";

const { width } = Dimensions.get('window');
const isMobile = width < 768;

const PANEL_WIDTH = Math.min(300, width * 0.8);

const NAV_ITEMS = [
	{ label: 'Why Choose MyTassel?', screen: 'WhyMyTassel' },
	{ label: 'Tutorials',            screen: 'Tutorials' },
	{ label: 'Blog',                 screen: 'Blog' },
	{ label: 'Checklists',           screen: 'Checklist', params: { type: 'graduation' } },
	{ label: 'Catalogue',            screen: 'Catalogue' },
	{ label: 'Privacy Policy',       screen: 'Privacy' },
	{ label: 'Terms & Conditions',   screen: 'Terms' },
	{ label: 'Contact Us',           screen: 'ContactUs' },
];

const ResourcesButton = () => {
	const navigation = useNavigation();
	const [panelOpen, setPanelOpen] = useState(false);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [hoveredItem, setHoveredItem] = useState(null);
	const panelAnim = useRef(new Animated.Value(PANEL_WIDTH)).current;
	const isECardGifRender = Platform.OS === "web" &&
		typeof window !== "undefined" &&
		new URLSearchParams(window.location.search).get("render") === "email-gif";

	// Block body scroll when mobile overlay is open
	useEffect(() => {
		if (typeof document === 'undefined') return;
		document.body.style.overflow = mobileOpen ? 'hidden' : '';
		return () => { document.body.style.overflow = ''; };
	}, [mobileOpen]);

	const openPanel = () => {
		setPanelOpen(true);
		Animated.spring(panelAnim, { toValue: 0, useNativeDriver: true, tension: 80, friction: 14 }).start();
	};

	const closePanel = () => {
		Animated.timing(panelAnim, { toValue: PANEL_WIDTH, duration: 220, useNativeDriver: true }).start(() => setPanelOpen(false));
	};

	const handlePress = () => {
		if (isMobile) setMobileOpen(true);
		else openPanel();
	};

	const navigateTo = (screen, params) => {
		closePanel();
		setMobileOpen(false);
		setTimeout(() => navigation.navigate(screen, params), isMobile ? 0 : 250);
	};

	if (isECardGifRender) return null;

	return (
		<>
			{/* Fixed bottom-right button */}
			<Animated.View style={[s.btn, hovered && s.btnHover]}>
				<Pressable
					onPress={handlePress}
					onMouseEnter={() => setHovered(true)}
					onMouseLeave={() => setHovered(false)}
					style={s.btnInner}
				>
					<Text style={s.btnText}>⋯</Text>
				</Pressable>
			</Animated.View>

			{/* Mobile full-screen overlay */}
			{isMobile && mobileOpen && (
				<View style={s.mobileOverlay}>
					<Pressable style={s.mobileClose} onPress={() => setMobileOpen(false)}>
						<Text style={s.mobileCloseText}>✕</Text>
					</Pressable>
					<ScrollView contentContainerStyle={s.mobileContent}>
						<Text style={s.overlayTitle}>Resources</Text>
						{NAV_ITEMS.map(item => (
							<Pressable key={item.screen} style={s.overlayItem} onPress={() => navigateTo(item.screen, item.params)}>
								<Text style={s.overlayItemText}>{item.label}</Text>
							</Pressable>
						))}
					</ScrollView>
				</View>
			)}

			{/* Desktop: backdrop + slide-in panel */}
			{!isMobile && panelOpen && (
				<Pressable style={s.backdrop} onPress={closePanel} />
			)}
			{!isMobile && (
				<Animated.View style={[s.panel, { transform: [{ translateX: panelAnim }] }]}>
					<Pressable style={s.panelClose} onPress={closePanel}>
						<Text style={s.panelCloseText}>✕</Text>
					</Pressable>
					<Text style={s.panelTitle}>Resources</Text>
					<View style={s.panelContent}>
						{NAV_ITEMS.map(item => (
							<Pressable
								key={item.screen}
								style={[s.panelItem, hoveredItem === item.screen && s.panelItemHover]}
								onMouseEnter={() => setHoveredItem(item.screen)}
								onMouseLeave={() => setHoveredItem(null)}
								onPress={() => navigateTo(item.screen, item.params)}
							>
								<Text style={s.panelItemText}>{item.label}</Text>
							</Pressable>
						))}
					</View>
				</Animated.View>
			)}
		</>
	);
};

const s = StyleSheet.create({
	btn: {
		position: 'fixed',
		bottom: isMobile ? 20 : 40,
		right: isMobile ? 20 : 40,
		zIndex: 9000,
		width: isMobile ? 36 : 40,
		height: isMobile ? 36 : 40,
		borderRadius: 20,
		backgroundColor: 'rgba(76, 149, 108, 0.9)',
		justifyContent: 'center',
		alignItems: 'center',
		borderWidth: 1,
		borderColor: 'rgba(255, 255, 255, 0.15)',
		shadowColor: '#1a5c3a',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.25,
		shadowRadius: 6,
		elevation: 6,
		transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
		cursor: 'pointer',
	},
	btnHover: {
		backgroundColor: '#3d8059',
		transform: [{ scale: 1.15 }, { translateY: -3 }],
		shadowOpacity: 0.4,
		shadowRadius: 10,
	},
	btnInner: {
		width: '100%',
		height: '100%',
		justifyContent: 'center',
		alignItems: 'center',
	},
	btnText: {
		color: '#fefde2',
		fontSize: isMobile ? 20 : 22,
		fontWeight: '600',
		textAlign: 'center',
	},
	// Mobile overlay
	mobileOverlay: {
		position: 'fixed',
		top: 0, left: 0, right: 0, bottom: 0,
		backgroundColor: 'rgba(76, 149, 108, 0.97)',
		zIndex: 10000,
		justifyContent: 'center',
		alignItems: 'center',
	},
	mobileClose: {
		position: 'absolute',
		top: 20, right: 20,
		width: 40, height: 40,
		borderRadius: 20,
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 10001,
	},
	mobileCloseText: { fontSize: 20, fontWeight: 'bold', color: '#fefde2' },
	mobileContent: { width: '80%', alignItems: 'center', paddingVertical: 60 },
	overlayTitle: {
		fontSize: 24, fontWeight: 'bold', color: '#fefde2',
		marginBottom: 24, fontFamily: 'Bodoni72Smallcaps',
	},
	overlayItem: {
		width: '100%', padding: 15, borderRadius: 10,
		marginBottom: 10, alignItems: 'center',
	},
	overlayItemText: {
		fontSize: 18, color: '#fefde2', textAlign: 'center',
		fontWeight: 'bold', fontFamily: 'Bodoni72Smallcaps',
	},
	// Desktop panel
	backdrop: {
		position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
		backgroundColor: 'rgba(0,0,0,0.45)',
		zIndex: 9998,
		backdropFilter: 'blur(4px)',
	},
	panel: {
		position: 'fixed', top: 0, right: 0,
		width: PANEL_WIDTH, height: '100%',
		backgroundColor: 'rgba(76, 149, 108, 0.97)',
		zIndex: 9999,
		boxShadow: '-5px 0 15px rgba(0,0,0,0.3)',
		padding: 20,
		flexDirection: 'column',
		overflow: 'hidden',
	},
	panelClose: {
		position: 'absolute', top: 20, right: 20,
		width: 40, height: 40, borderRadius: 20,
		justifyContent: 'center', alignItems: 'center',
		zIndex: 10000,
	},
	panelCloseText: { fontSize: 24, fontWeight: 'bold', color: '#fefde2' },
	panelTitle: {
		fontSize: 30, fontWeight: 'bold', color: '#fefde2',
		marginTop: 40, marginBottom: 30,
		fontFamily: 'Bodoni72Smallcaps', textAlign: 'center',
	},
	panelContent: { flex: 1, alignItems: 'center', overflowY: 'auto' },
	panelItem: {
		width: '100%', padding: 15, marginBottom: 10,
		borderRadius: 10, alignItems: 'center',
		transition: 'all 0.2s ease',
		cursor: 'pointer',
	},
	panelItemHover: {
		backgroundColor: 'rgba(255,255,255,0.1)',
		transform: [{ scale: 1.04 }],
	},
	panelItemText: {
		fontSize: 20, color: '#fefde2', textAlign: 'center',
		fontWeight: 'bold', fontFamily: 'Bodoni72Smallcaps',
	},
});

export default ResourcesButton;
